/**
 * DemoFilterContext — Sprint 13.1.
 *
 * Global toggle for showing/hiding is_test=true (demo / test) rows in
 * list pages, reports, and exports. Default is hidden.
 *
 * Persists to localStorage so the choice survives reloads. It is clamped off
 * outside preview and only explicitly authorized diagnostic users see it.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { isPreviewEnvironment } from "../lib/preview";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";

type DemoFilterContextValue = {
  showDemo: boolean;
  canUseDemoData: boolean;
  setShowDemo: (v: boolean) => void;
  queryParam: () => string; // for inclusion in API calls
};

const Ctx = createContext<DemoFilterContextValue | null>(null);
const LS_KEY = "wst.showDemo";
export const demoDataEnvironmentEnabled = isPreviewEnvironment;

export const DemoFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [runtimeSupportsDemoData, setRuntimeSupportsDemoData] = useState(false);
  const hasDiagnosticPermission = useMemo(
    () => new Set(
      (Array.isArray(user?.permissions) ? user.permissions : [])
        .map((code: string) => code.replace(":", ".").toLowerCase()),
    ).has("diagnostics.test_data"),
    [user?.permissions],
  );
  const demoDataAuthorized =
    demoDataEnvironmentEnabled && runtimeSupportsDemoData && hasDiagnosticPermission;
  const [showDemo, setShowDemoState] = useState<boolean>(() => {
    if (!demoDataEnvironmentEnabled) return false;
    try {
      const v = localStorage.getItem(LS_KEY);
      return v === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!demoDataEnvironmentEnabled) {
      setRuntimeSupportsDemoData(false);
      return;
    }
    let active = true;
    api<{ training_authority?: { mutation_authorized?: boolean } }>("/api/v1/health")
      .then((health) => {
        if (active) {
          setRuntimeSupportsDemoData(
            health.training_authority?.mutation_authorized === true,
          );
        }
      })
      .catch(() => {
        if (active) setRuntimeSupportsDemoData(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const setShowDemo = useCallback((v: boolean) => {
    const allowedValue = demoDataAuthorized && v;
    setShowDemoState(allowedValue);
    try {
      if (allowedValue) localStorage.setItem(LS_KEY, "1");
      else localStorage.removeItem(LS_KEY);
    } catch {
      // localStorage may be disabled; in-memory only
    }
  }, [demoDataAuthorized]);

  useEffect(() => {
    if (demoDataAuthorized) return;
    setShowDemoState(false);
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      // localStorage may be disabled.
    }
  }, [demoDataAuthorized]);

  const authorizedShowDemo = demoDataAuthorized && showDemo;
  const queryParam = useCallback(
    () => (authorizedShowDemo ? "include_test=true" : "include_test=false"),
    [authorizedShowDemo],
  );

  const value = useMemo(
    () => ({
      showDemo: authorizedShowDemo,
      canUseDemoData: demoDataAuthorized,
      setShowDemo,
      queryParam,
    }),
    [authorizedShowDemo, demoDataAuthorized, setShowDemo, queryParam],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useDemoFilter = (): DemoFilterContextValue => {
  const v = useContext(Ctx);
  if (!v) {
    // Fallback: hidden. App should always have the provider mounted, but
    // tests / storybook may not.
    return {
      showDemo: false,
      canUseDemoData: false,
      setShowDemo: () => {},
      queryParam: () => "include_test=false",
    };
  }
  return v;
};

/**
 * Visibility helper. We don't read auth here to keep this context decoupled;
 * Layout passes the explicit effective permission decision.
 */
export const useDemoFilterVisible = (hasDiagnosticPermission: boolean): boolean =>
  demoDataEnvironmentEnabled && hasDiagnosticPermission;
